import { Link } from 'react-router-dom';
import { LogoIcon } from '@/assets/icons';
import { ROUTES } from '@/constants';
import { cn } from '@/libs/utils';
import TruncateTooltipCell from '../data-table/TruncateTooltipCell';
import { Header } from '../layouts';
import { TextSkeleton } from '../skeletons';

export default function ContentHeader({
  className,
  label,
  title,
  isLoading,
  children,
  style,
}) {
  return (
    <Header className={cn('content-header', className)} style={style}>
      <div className="flex items-center justify-between gap-4">
        {title ? (
          // 🚨 `min-w-0` on both, or the title never gives way. A flex/grid item
          // defaults to `min-width: auto`, which refuses to shrink below its own
          // content — so on a narrow screen the h3 keeps its full width and, being
          // `whitespace-nowrap`, simply runs underneath the toolbar. The ellipsis
          // machinery in TruncateTooltipCell is already there; it just never got a
          // constrained box to work in.
          <div className="grid min-w-0 gap-1.5">
            {label && (
              <span className="text-[13px] font-semibold text-zinc-200">
                {label}
              </span>
            )}

            {!isLoading ? (
              <h3 className="flex min-w-0 items-center whitespace-nowrap text-xl font-bold md:text-2xl">
                <TruncateTooltipCell label={title} maxLength={36} />
              </h3>
            ) : (
              <TextSkeleton />
            )}
          </div>
        ) : (
          <div className="relative mx-auto grid justify-center">
            {!isLoading && (
              <Link to={ROUTES.admin}>
                <LogoIcon />
              </Link>
            )}
          </div>
        )}

        {/*
          🚨 One row, and it must stay one row. The header is fixed at
          `--headerHeight` with `overflow-y-hidden`, so a toolbar allowed to wrap
          grows past the box it lives in and — being vertically centred — climbs
          out of the top and paints over the table's own header. Measured on the
          invoice book at 1.150px: two lines, 76px of toolbar in a 68px header.
          Crowded screens scroll sideways instead; that is what `overflow-x-auto`
          on `.content-header` is for, and it is how every other table behaves.
          `shrink-0` is what makes that scroll work — without it the buttons
          squeeze themselves into illegible slivers rather than overflow.
        */}
        <div className="flex items-center justify-end gap-3 [&>*]:shrink-0">
          {children}
        </div>
      </div>
    </Header>
  );
}
